<script setup lang="ts">
    declare interface ProductToggleProps {
        toggleId: string;
    }
    type ToggleData = {
        label: string;
        page: string;
        link?: Link; // remove in https://onewoom.atlassian.net/browse/ECOMM-2792
    };

    const props = defineProps<ProductToggleProps>();

    const appStore = useAppStore();
    const nuxtApp = useNuxtApp();
    const route = useRoute();
    const { isUrlCurrentPath, resolveUrlLink } = useURL();

    const getToggle = ({ label, page, link }: ToggleData) => {
        // remove link in https://onewoom.atlassian.net/browse/ECOMM-2792
        const { url } = resolveUrlLink(page ? { type: 'page', data: page, label: '', clickType: null } : link);

        if (!url || !label) return;

        return {
            label,
            url,
            selected: isUrlCurrentPath(url),
        };
    };

    const toggle = computed(() => {
        const toggleData = appStore.getReference<{ id: string; left: ToggleData; right: ToggleData }>(props.toggleId);
        if (!toggleData) return;

        const left = getToggle(toggleData.left);
        const right = getToggle(toggleData.right);
        if (!left || !right) return undefined;

        return {
            left,
            right,
        };
    });

    function addVariant(url: string) {
        const selectedVariant = route.query.variant;
        if (selectedVariant) url += `?variant=${selectedVariant}`;
        return url;
    }

    const trackClick = (label?: string) => {
        nuxtApp.$gtm.selectItem({
            item_list_id: 'product_toggle',
            items: [
                {
                    item_name: label || '',
                },
            ],
        });
    };
</script>
<template>
    <div
        v-if="toggle"
        class="mol-product-toggle flex w-full">
        <atm-button
            :to="addVariant(toggle.left.url)"
            kind="link"
            block
            :class="['w-1/2 rounded-r-none', { 'pointer-events-none': toggle.left.selected }]"
            :aria-current="toggle.left.selected ? 'page' : undefined"
            :styling="toggle.left.selected ? 'solid-secondary' : 'outline'"
            @click="trackClick(toggle.left.label)">
            <span v-if="toggle.left.label">
                <renderer-html :content="toggle.left.label" />
            </span>
        </atm-button>
        <atm-button
            :to="addVariant(toggle.right.url)"
            kind="link"
            block
            :class="['w-1/2 rounded-l-none', { 'pointer-events-none': toggle.right.selected }]"
            :aria-current="toggle.right.selected ? 'page' : undefined"
            :styling="toggle.right.selected ? 'solid-secondary' : 'outline'"
            @click="trackClick(toggle.right.label)">
            <span v-if="toggle.right.label">
                <renderer-html :content="toggle.right.label" />
            </span>
        </atm-button>
    </div>
</template>
